import Quill from 'quill'

const Inline = Quill.import('blots/inline')

/**
 * Custom Quill blot for note links
 * Creates clickable note references in the editor
 */
class NoteLinkBlot extends Inline {
  static blotName = 'noteLink'
  static tagName = 'span'
  static className = 'note-link'
  
  static create(data) {
    const node = super.create()
    node.setAttribute('data-note-id', data.noteId)
    node.setAttribute('data-note-title', data.title)
    node.className = 'note-link'
    
    // Apply base styling (theme-specific styles handled by CSS)
    node.style.cssText = `
      padding: 2px 6px;
      border-radius: 4px;
      cursor: pointer;
      text-decoration: none;
      border: 1px solid;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      font-weight: 500;
      white-space: nowrap;
    `
    
    return node
  }
  
  static formats(node) {
    return {
      noteId: node.getAttribute('data-note-id'),
      title: node.getAttribute('data-note-title')
    }
  }
  
  format(name, value) {
    if (name === 'noteLink' && value) {
      this.domNode.setAttribute('data-note-id', value.noteId)
      this.domNode.setAttribute('data-note-title', value.title)
    } else {
      super.format(name, value)
    }
  }
}

/**
 * Quill module for @ mention detection and note linking
 * Handles text change events and shows floating button when @ is detected
 */
class NoteMentionModule {
  static moduleName = 'noteMention'
  constructor(quill, options = {}) {
    this.quill = quill
    this.options = {
      onMentionButtonClick: () => {},
      isSensitiveContext: false,
      ...options
    }
    
    this.isShowingButton = false
    this.mentionButton = null
    this.currentMentionIndex = null
    this.hideTimeout = null
    
    // Bind event handlers
    this.handleTextChange = this.handleTextChange.bind(this)
    this.handleSelectionChange = this.handleSelectionChange.bind(this)
    this.handleButtonClick = this.handleButtonClick.bind(this)
    
    // Listen for text changes and cursor movements
    this.quill.on('text-change', this.handleTextChange)
    this.quill.on('selection-change', this.handleSelectionChange)
    
    // Clean up on destroy
    this.quill.on('editor-change', (eventType) => {
      if (eventType === 'text-change' || eventType === 'selection-change') {
        // Handle editor changes
      }
    })
  }
  
  /**
   * Handle text changes to detect @ symbol
   */
  handleTextChange(delta, oldDelta, source) {
    if (source !== 'user') return
    
    // Check if we're in a sensitive context - disable @ mentions
    if (this.options.isSensitiveContext) {
      console.log('🔒 NoteMention: @ mentions disabled in sensitive context')
      this.hideMentionButton()
      return
    }
    
    const range = this.quill.getSelection()
    if (!range) return
    
    // Get text around cursor (look back 20 characters)
    const startIndex = Math.max(0, range.index - 20)
    const text = this.quill.getText(startIndex, 20)
    
    // Find the last @ symbol in the text
    const lastAtIndex = text.lastIndexOf('@')
    
    if (lastAtIndex !== -1) {
      // Calculate the actual position of @ in the document
      const actualAtIndex = startIndex + lastAtIndex
      
      // Get text after @ symbol
      const textAfterAt = text.substring(lastAtIndex + 1)
      
      // Show button if @ is at end or followed by word characters only
      // This allows for partial typing like "@meet" or "@project"
      if (textAfterAt === '' || /^\w*$/.test(textAfterAt)) {
        // Check if cursor is right after the @ or the partial word
        const expectedCursorPos = actualAtIndex + 1 + textAfterAt.length
        
        if (range.index === expectedCursorPos) {
          this.showMentionButton(actualAtIndex, textAfterAt)
          return
        }
      }
    }
    
    // Hide button if @ not found or conditions not met
    this.hideMentionButton()
  }
  
  /**
   * Handle selection changes (cursor movement)
   */
  handleSelectionChange(range, oldRange, source) {
    if (!range) {
      this.hideMentionButton()
      return
    }
    
    // If button is showing and cursor moved away from @ context, hide it
    if (this.isShowingButton && this.currentMentionIndex !== null) {
      const text = this.quill.getText(this.currentMentionIndex, range.index - this.currentMentionIndex + 1)
      
      // Hide if cursor moved away from @ context
      if (!text.includes('@') || range.index < this.currentMentionIndex) {
        this.hideMentionButton()
      }
    }
  }
  
  /**
   * Show the floating mention button
   */
  showMentionButton(atIndex, partialText = '') {
    if (this.isShowingButton && this.currentMentionIndex === atIndex) {
      // Button already showing for this @ symbol, just update timeout
      this.resetHideTimeout()
      return
    }
    
    // Hide any existing button first
    this.hideMentionButton()
    
    // Store the @ position
    this.currentMentionIndex = atIndex
    
    // Detect mobile and theme
    const isMobile = window.innerWidth < 768
    const isAmoled = document.documentElement.classList.contains('amoled')
    const isDark = document.documentElement.classList.contains('dark')
    
    // Get cursor position relative to editor
    const bounds = this.quill.getBounds(atIndex + 1) // Position after @
    
    // Create floating button
    this.mentionButton = document.createElement('button')
    this.mentionButton.className = 'note-mention-button'
    this.mentionButton.innerHTML = '🔗'
    this.mentionButton.title = partialText ? `Link note (searching: "${partialText}")` : 'Link to note'
    this.mentionButton.type = 'button'
    
    // Mobile-optimized sizing
    const buttonSize = isMobile ? 44 : 32
    const offsetY = isMobile ? 50 : 35
    
    // Calculate position (ensure it stays within viewport)
    const editorRect = this.quill.container.getBoundingClientRect()
    const leftPos = Math.max(10, Math.min(bounds.left, window.innerWidth - buttonSize - 10))
    const topPos = Math.max(10, bounds.top - offsetY)
    
    // Theme-aware colors
    let buttonStyles = ''
    if (isAmoled) {
      buttonStyles = `
        background: linear-gradient(135deg, #000000 0%, #111111 100%);
        border: 2px solid #374151;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.05);
      `
    } else if (isDark) {
      buttonStyles = `
        background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
        border: 2px solid #4b5563;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1);
      `
    } else {
      buttonStyles = `
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        border: 2px solid #3b82f6;
        color: #1f2937;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px rgba(59,130,246,0.1);
      `
    }
    
    this.mentionButton.style.cssText = `
      position: absolute;
      left: ${leftPos}px;
      top: ${topPos}px;
      width: ${buttonSize}px;
      height: ${buttonSize}px;
      border-radius: ${buttonSize / 2}px;
      cursor: pointer;
      z-index: 1000;
      font-size: ${isMobile ? 18 : 16}px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      touch-action: manipulation;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      outline: none;
      user-select: none;
      ${buttonStyles}
    `
    
    // Add interaction effects
    this.addButtonInteractions(isMobile, isAmoled, isDark)
    
    // Add click handler
    this.mentionButton.addEventListener('click', this.handleButtonClick)
    
    // Add to editor container
    this.quill.container.style.position = 'relative'
    this.quill.container.appendChild(this.mentionButton)
    this.isShowingButton = true
    
    // Set auto-hide timeout (longer on mobile)
    this.resetHideTimeout(isMobile ? 5000 : 3000)
    
    console.log(`🔗 NoteMention: Showing button at position ${atIndex}${partialText ? ` (partial: "${partialText}")` : ''}`)
  }
  
  /**
   * Add interaction effects to the button
   */
  addButtonInteractions(isMobile, isAmoled, isDark) {
    if (isMobile) {
      // Mobile touch interactions
      this.mentionButton.addEventListener('touchstart', (e) => {
        e.preventDefault()
        if (navigator.vibrate) {
          navigator.vibrate(50) // Light haptic feedback
        }
        this.mentionButton.style.transform = 'scale(0.95)'
      })
      
      this.mentionButton.addEventListener('touchend', () => {
        this.mentionButton.style.transform = 'scale(1)'
      })
      
      this.mentionButton.addEventListener('touchmove', (e) => {
        e.preventDefault()
      })
    } else {
      // Desktop hover interactions
      this.mentionButton.addEventListener('mouseenter', () => {
        if (isAmoled) {
          this.mentionButton.style.background = 'linear-gradient(135deg, #111111 0%, #1a1a1a 100%)'
        } else if (isDark) {
          this.mentionButton.style.background = 'linear-gradient(135deg, #374151 0%, #4b5563 100%)'
        } else {
          this.mentionButton.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }
        this.mentionButton.style.transform = 'scale(1.05)'
      })
      
      this.mentionButton.addEventListener('mouseleave', () => {
        if (isAmoled) {
          this.mentionButton.style.background = 'linear-gradient(135deg, #000000 0%, #111111 100%)'
        } else if (isDark) {
          this.mentionButton.style.background = 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
        } else {
          this.mentionButton.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
        }
        this.mentionButton.style.transform = 'scale(1)'
      })
    }
  }
  
  /**
   * Handle button click
   */
  handleButtonClick(e) {
    e.preventDefault()
    e.stopPropagation()
    
    if (this.currentMentionIndex !== null) {
      // Get any partial text after @
      const range = this.quill.getSelection()
      if (range) {
        const text = this.quill.getText(this.currentMentionIndex, range.index - this.currentMentionIndex + 1)
        const atMatch = text.match(/@(\w*)/)
        const partialText = atMatch ? atMatch[1] : ''
        
        // Call the callback with mention position and partial text
        this.options.onMentionButtonClick(this.currentMentionIndex, partialText)
      }
    }
    
    this.hideMentionButton()
  }
  
  /**
   * Hide the mention button
   */
  hideMentionButton() {
    if (this.mentionButton) {
      this.mentionButton.remove()
      this.mentionButton = null
    }
    
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    
    this.isShowingButton = false
    this.currentMentionIndex = null
  }
  
  /**
   * Reset the auto-hide timeout
   */
  resetHideTimeout(delay = 3000) {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
    
    this.hideTimeout = setTimeout(() => {
      this.hideMentionButton()
    }, delay)
  }
  
  /**
   * Insert a note link at the specified position
   */
  insertNoteLink(atIndex, noteData, partialText = '') {
    if (!this.quill) return
    
    // Calculate the text to replace (@ + partial text)
    const replaceLength = 1 + partialText.length // @ + partial text
    
    // Delete the @ and partial text
    this.quill.deleteText(atIndex, replaceLength)
    
    // Insert the note link
    const linkText = `@${noteData.title}`
    this.quill.insertText(atIndex, linkText, 'noteLink', noteData)
    
    // Add a space after the link and position cursor there
    this.quill.insertText(atIndex + linkText.length, ' ')
    this.quill.setSelection(atIndex + linkText.length + 1)
    
    console.log(`🔗 NoteMention: Inserted link to "${noteData.title}" at position ${atIndex}`)
  }
  
  /**
   * Cleanup method
   */
  destroy() {
    this.hideMentionButton()
    
    if (this.quill) {
      this.quill.off('text-change', this.handleTextChange)
      this.quill.off('selection-change', this.handleSelectionChange)
    }
  }
}

// Export both the blot and module
export { NoteLinkBlot, NoteMentionModule }

// Default export for the module
export default NoteMentionModule
